import * as React from 'react';
import { connectSsr } from '@bart.sk-eshop/ssr-service';
import NotFound from '../Errors/NotFound';
import API from '../../services/API';
import ProductContainer, { ProductProps } from '../Product/Product';
import styled from 'styled-components';
import Category, { CategoryProps } from '../Category/Category';
import CMS, { CmsProps } from '../CMS/CMS';
import CMSSectionArticles from '../CMS/CMSSectionArticles';
import { connect } from 'react-redux';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import {
  HorizontalCenterLoader,
  LoaderWrapper,
} from '../../components/_helpers/Loader/Loader';
import { loadCategory } from '../Category/actions';
import { loadUrlTranslations, setNotFound } from '../App/actions';
import { resolveLangByHostname } from '../../configureTrans';

const URL_TRANSLATION_TYPES = {
  product: 'product',
  category: 'category',
  sitemap: 'sitemap',
  sitemap_with_content: 'sitemap',
};

enum UrlType {
  Product,
  NotFound,
  Category,
  CategoryWithSubcategories,
  Sitemap,
  SitemapWithContent,
  SitemapArticleList,
  Printer,
  FooterArticle,
}

interface Props {
  urlType: UrlType;
  isPreview: boolean;
  childProps: object;
  loading: boolean;
  isError: boolean;
  token: any;
  lang: string;
}

interface ChildProps {
  urlType: UrlType;
  childProps: ProductProps | CategoryProps | null;
}

class UrlMapper extends React.Component<Props, {}> {
  public static defaultProps = {
    loading: true,
    isError: false,
  };

  public static async getInitialProps(props) {
    let newProps: ChildProps;
    let isPreview: boolean = false;

    const correctLang = props.lang || resolveLangByHostname(props.hostname);
    const pathnameDecoded = decodeURI(
      props.location.pathname.replace('/preview/', ''),
    );
    try {
      // isPreview = UrlMapper.isPreview(props.location.pathname);
      const urlMapData = await API.getUrlMap(
        {
          url: pathnameDecoded,
        },
        { xAcceptLanguage: correctLang },
      );

      const { data, type } = urlMapData;

      const urlTranslationType = URL_TRANSLATION_TYPES[type];
      if (urlTranslationType) {
        await props.dispatch(
          loadUrlTranslations(
            props.location.pathname,
            pathnameDecoded,
            urlTranslationType,
          ),
        );
      }

      switch (type) {
        case 'product': {
          newProps = await UrlMapper.getProductChildProps(
            data.product_id,
            props,
          );
          break;
        }
        case 'category': {
          newProps = await UrlMapper.getCategoryChildProps(
            data.category_id,
            props,
          );
          break;
        }
        case 'sitemap': {
          if (await UrlMapper.hasArticles(data.sitemap_id, correctLang)) {
            newProps = await UrlMapper.getSitemapArticleListChildProps(
              data.sitemap_id,
              correctLang,
              props,
            );
            break;
          }
          newProps = await UrlMapper.getSitemapWithContentChildProps(
            data.sitemap_id,
            0,
            correctLang,
            props.dispatch,
          );
          // if (
          //   data &&
          //   data.sitemap_id &&
          //   data.sitemap_id >= 139 &&
          //   data.sitemap_id <= 141
          // ) {
          //   newProps = await UrlMapper.getFooterContentChildProps(
          //     data.sitemap_id,
          //     props,
          //   );
          // } else {
          //   newProps = await UrlMapper.getSitemapChildProps(
          //     data.sitemap_id,
          //     props,
          //   );
          // }
          break;
        }
        case 'sitemap_with_content': {
          newProps = await UrlMapper.getSitemapWithContentChildProps(
            null,
            data.content_id,
            correctLang,
            props.dispatch,
          );
          break;
        }
        default: {
          await props.dispatch(setNotFound(true));
          newProps = UrlMapper.getNotFoundChildProps();
          break;
        }
      }
    } catch (exp) {
      await props.dispatch(setNotFound(true));
      return {
        isError: true,
      };
    }
    return { ...newProps, isPreview };
  }

  private static getProductChildProps = async (productId, props) => {
    const productProps = {
      product_id: productId,
      ...props,
    };
    return {
      urlType: UrlType.Product,
      childProps: await ProductContainer.getInitialProps(productProps),
    };
  };

  private static hasArticles = async (sitemapId, lang) => {
    if (!sitemapId) {
      return false;
    }
    try {
      const articles = await API.loadArticles(
        { sitemapId, limit: 1, sfForm: 'Article', isPublished: '1' },
        { xAcceptLanguage: lang },
      );
      return (articles && articles.total ? articles.total : 0) > 0;
    } catch (exp) {
      return false;
    }
  };

  private static getSitemapArticleListChildProps = async (
    sitemapId,
    lang,
    props,
  ) => {
    return {
      urlType: UrlType.SitemapArticleList,
      childProps: await CMSSectionArticles.getInitialProps({
        ...props,
        sitemapId,
        lang,
      }),
    };
  };

  private static getSitemapWithContentChildProps = async (
    sitemapId,
    contentId,
    lang,
    dispatch,
  ) => {
    return {
      urlType: UrlType.SitemapWithContent,
      childProps: await CMS.getInitialProps({
        sitemapId,
        contentId,
        lang,
        dispatch,
      }),
    };
  };

  private static getFooterContentChildProps = async (sitemapId, props) => {
    return {
      urlType: UrlType.FooterArticle,
      childProps: props,
    };
  };

  private static getSitemapChildProps = async (sitemapId, props) => {
    return {
      urlType: UrlType.Sitemap,
      childProps: await Category.getInitialProps(
        {
          sitemapId,
        },
        props,
      ),
    };
  };

  private static getCategoryChildProps = async (categoryId, props) => {
    const categoryProps = {
      category_id: categoryId,
      ...props,
    };

    // const { dispatch } = props;
    // dispatch(loadCategory(categoryProps.category_id || 0));
    return {
      urlType: UrlType.Category,
      childProps: await Category.getInitialProps(categoryProps),
    };

    // currently, there is no difference between CLASSIC
    // AND CLASSIC_WITH_SUBCATEGORIES
    // const categoryData = await API.loadCategory(
    //   categoryProps.category_id || 0,
    //   {},
    // );

    // if (categoryData.type === 'CLASSIC') {
    //   return {
    //     urlType: UrlType.Category,
    //     childProps: await Category.getInitialProps(categoryProps, props),
    //   };
    // } else {
    //   return {
    //     urlType: UrlType.Category,
    //     childProps: await Category.getInitialProps(categoryProps, props),
    //   };
    // }
  };

  private static getNotFoundChildProps = () => {
    return {
      urlType: UrlType.NotFound,
      childProps: null,
    };
  };

  private static getLoaderView = () => {
    return (
      <LoaderWrapper>
        <HorizontalCenterLoader />
      </LoaderWrapper>
    );
  };

  private static getNotFoundView = () => {
    return <NotFound />;
  };

  private static isPreview = (url: string): boolean => {
    return url.match(/\/preview\/.+/) ? true : false;
  };

  public render() {
    const { isError, urlType, loading } = this.props;

    if (isError) {
      return UrlMapper.getNotFoundView();
    }

    if (loading) {
      return UrlMapper.getLoaderView();
    }

    if (
      urlType === UrlType.Category ||
      urlType === UrlType.CategoryWithSubcategories
    ) {
      return this.getCategoryView();
    } else if (urlType === UrlType.Sitemap) {
      return this.getCMSView();
    } else if (urlType === UrlType.FooterArticle) {
      return this.getFooterArticleView();
    } else if (urlType === UrlType.SitemapArticleList) {
      return this.getSitemapArticleListView();
    } else if (urlType === UrlType.SitemapWithContent) {
      return this.getCMSView();
    } else if (urlType === UrlType.Product) {
      return this.getProductView();
    }
    return UrlMapper.getNotFoundView();
  }

  private getFooterArticleView = () => {
    const childProps: CategoryProps = this.props.childProps as CategoryProps;
    return <div>footer arricle</div>;
  };

  private getCategoryView = () => {
    const childProps: CategoryProps = this.props.childProps as CategoryProps;
    return <Category {...childProps} loading={this.props.loading} />;
  };

  private getSitemapArticleListView = () => {
    return <CMSSectionArticles {...(this.props.childProps as any)} />;
  };

  private getCMSView = () => {
    const childProps: CmsProps = this.props.childProps as CmsProps;

    return (
      <>
        {this.props.isPreview && (
          <UpperBar>
            {__(
              'Toto je náhľad stránky. Ak je stránka v administrácii označená ako “Zobrazená” je tento náhľad verejne dostupný.',
            )}
          </UpperBar>
        )}
        <CMS {...childProps} />
      </>
    );
  };

  private getProductView = () => {
    const childProps: ProductProps = this.props.childProps as ProductProps;
    return (
      <ProductContainer
        {...childProps}
        location={(this.props as any).location}
      />
    );
  };
}

const UpperBar = styled.div`
  width: 100%;
  height: ${rem(40)};
  color: white;
  background: red;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  z-index: 400;
`;

const mapStateToProps = state => {
  return {
    defaultTitle: state.general.title,
    token: state.auth.token,
    lang: state.general.lang,
  };
};

export default connect(mapStateToProps)(
  connectSsr({ displayName: 'UrlMapper' })(UrlMapper),
);
